'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HotspotData {
  id: number;
  title: string;
  position: {top: string;left: string;};
  specifications: {
    label: string;
    value: string;
  }[];
}

const hotspots: HotspotData[] = [
{
  id: 1,
  title: 'Pre-Treatment Zone',
  position: { top: '35%', left: '15%' },
  specifications: [
  { label: 'Chemistry', value: 'Zirconium Conversion Coating' },
  { label: 'Temperature', value: '120-140°F (49-60°C)' },
  { label: 'Immersion Time', value: '90-120 seconds' },
  { label: 'pH Control', value: '4.5-5.5 ±0.2' },
  { label: 'Coating Weight', value: '50-150 mg/ft²' }]

},
{
  id: 2,
  title: 'Spray Booth',
  position: { top: '40%', left: '50%' },
  specifications: [
  { label: 'Voltage', value: '60-100 kV Electrostatic' },
  { label: 'Powder Type', value: 'Epoxy/Polyester Hybrid' },
  { label: 'Film Thickness', value: '2-4 mils (50-100 μm)' },
  { label: 'Transfer Efficiency', value: '85-95%' },
  { label: 'Booth Airflow', value: '12,000 CFM HEPA Filtered' }]

},
{
  id: 3,
  title: 'Cure Oven',
  position: { top: '30%', left: '85%' },
  specifications: [
  { label: 'Temperature', value: '375-400°F (190-204°C)' },
  { label: 'Cure Time', value: '15-20 minutes' },
  { label: 'Uniformity', value: '±5°F across zone' },
  { label: 'Heating Method', value: 'Gas-Fired Convection' },
  { label: 'Capacity', value: '500 ft²/hour throughput' }]

}];


const InteractiveConveyor = () => {
  const [activeHotspot, setActiveHotspot] = useState<number | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const handleHotspotClick = (id: number) => {
    if (!isHydrated) return;
    setActiveHotspot(activeHotspot === id ? null : id);
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full mb-4">
            <Icon name="CursorArrowRaysIcon" size={20} variant="outline" className="text-accent" />
            <span className="text-sm font-cta font-semibold text-accent">
              Interactive Equipment Tour
            </span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Conveyor System Deep Dive
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Click the hotspots to explore technical specifications of our automated powder coating line.
          </p>
        </div>

        <div className="relative bg-card rounded-xl shadow-lg overflow-hidden">
          <div className="relative aspect-[16/9]">
            <AppImage
              src="https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/FFC%20site%20images/Page5Conveyor.jpg"
              alt="Industrial powder coating conveyor line showing pre-treatment tanks, spray booth, and cure oven in sequence"
              className="w-full h-full object-cover" />


            <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />

            {hotspots.map((hotspot) =>
            <div key={hotspot.id}>
                <button
                onClick={() => handleHotspotClick(hotspot.id)}
                className="absolute w-12 h-12 bg-accent rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all duration-base cursor-pointer border-4 border-accent-foreground/20 animate-pulse hover:animate-none"
                style={{ top: hotspot.position.top, left: hotspot.position.left }}
                aria-label={`View ${hotspot.title} specifications`}>

                  <Icon name="PlusIcon" size={24} variant="solid" className="text-accent-foreground" />
                </button>

                {isHydrated && activeHotspot === hotspot.id &&
              <div
                className="absolute z-10 w-80 bg-card rounded-lg shadow-xl border border-border p-6"
                style={{
                  top: `calc(${hotspot.position.top} + 60px)`,
                  left: hotspot.id === 3 ? 'auto' : hotspot.position.left,
                  right: hotspot.id === 3 ? '5%' : 'auto',
                  transform: hotspot.id === 3 ? 'none' : 'translateX(-50%)'
                }}>

                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-headline font-bold text-foreground">
                        {hotspot.title}
                      </h3>
                      <button
                    onClick={() => setActiveHotspot(null)}
                    className="p-1 hover:bg-muted rounded transition-colors"
                    aria-label="Close specifications">

                        <Icon name="XMarkIcon" size={20} variant="outline" className="text-muted-foreground" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      {hotspot.specifications.map((spec, index) =>
                  <div key={index} className="flex justify-between items-start">
                          <span className="text-sm font-body text-muted-foreground">
                            {spec.label}:
                          </span>
                          <span className="text-sm font-cta font-semibold text-foreground text-right ml-4">
                            {spec.value}
                          </span>
                        </div>
                  )}
                    </div>
                  </div>
              }
              </div>
            )}
          </div>

          <div className="p-6 bg-primary/5 border-t border-border">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="InformationCircleIcon" size={20} variant="outline" />
              <span>Click the pulse points to reveal detailed technical specifications</span>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default InteractiveConveyor;